import { supabase } from '@/integrations/supabase/client';
import { ensureSession } from '@/lib/engagementPoints';

export type ReplyFeedSource = 'announcement_replies' | 'announcement_responses' | 'thread_messages';

export interface OwnerReplyFeedItem {
  id: string;
  announcement_id: string;
  recipient_name: string;
  public_slug: string | null;
  text: string;
  created_at: string;
  source: ReplyFeedSource;
  thread_id?: string;
  sender_role?: 'owner' | 'participant';
}

export interface AnnouncementReplyTimeline {
  announcement: {
    id: string;
    recipient_name: string;
    message: string | null;
    template_name: string | null;
    public_slug: string | null;
  } | null;
  directReplies: Array<{ id: string; reply_text: string; created_at: string }>;
  threadMessages: Array<{
    id: string;
    thread_id: string;
    sender_role: 'owner' | 'participant';
    message: string;
    created_at: string;
  }>;
}

async function readRows<T>(label: string, request: PromiseLike<{ data: unknown; error: unknown }>): Promise<T[]> {
  const { data, error } = await request;
  if (error) {
    console.warn(`[Replies] ${label} failed:`, error);
    return [];
  }
  return Array.isArray(data) ? (data as T[]) : [];
}

export async function fetchOwnerReplyFeed(userId: string, limit = 200): Promise<OwnerReplyFeedItem[]> {
  // Owner-only reads: without the sign-in token these hit "permission denied".
  if (!(await ensureSession())) return [];
  const { data: announcements, error } = await (supabase as any)
    .from('announcements')
    .select('id, recipient_name, public_slug, status')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  const owned = ((announcements as any[]) || []).filter((a) => a?.id);
  const ids = owned.map((a) => a.id);
  if (ids.length === 0) return [];

  const nameMap = new Map<string, { recipient_name: string; public_slug: string | null }>(
    owned.map((a) => [a.id, { recipient_name: a.recipient_name || 'Someone', public_slug: a.public_slug || null }]),
  );

  const [directReplies, legacyResponses, threads] = await Promise.all([
    readRows<any>(
      'announcement_replies',
      (supabase as any)
        .from('announcement_replies')
        .select('id, announcement_id, reply_text, created_at')
        .in('announcement_id', ids)
        .order('created_at', { ascending: false })
        .limit(limit),
    ),
    readRows<any>(
      'announcement_responses',
      (supabase as any)
        .from('announcement_responses')
        .select('id, announcement_id, response_message, created_at')
        .in('announcement_id', ids)
        .order('created_at', { ascending: false })
        .limit(limit),
    ),
    readRows<any>(
      'conversation_threads',
      (supabase as any)
        .from('conversation_threads')
        .select('id, announcement_id')
        .eq('announcement_owner_id', userId)
        .in('announcement_id', ids),
    ),
  ]);

  const threadIds = threads.map((t) => t.id).filter(Boolean);
  const threadAnnMap = new Map<string, string>(threads.map((t) => [t.id, t.announcement_id]));
  const threadMessages = threadIds.length
    ? await readRows<any>(
        'thread_messages',
        (supabase as any)
          .from('thread_messages')
          .select('id, thread_id, message, created_at, sender_role')
          .in('thread_id', threadIds)
          .eq('sender_role', 'participant')
          .order('created_at', { ascending: false })
          .limit(limit),
      )
    : [];

  const feed: OwnerReplyFeedItem[] = [
    ...directReplies.map((r) => {
      const ann = nameMap.get(r.announcement_id);
      return {
        id: r.id,
        announcement_id: r.announcement_id,
        recipient_name: ann?.recipient_name || 'Someone',
        public_slug: ann?.public_slug || null,
        text: r.reply_text || '',
        created_at: r.created_at,
        source: 'announcement_replies' as const,
      };
    }),
    ...legacyResponses.map((r) => {
      const ann = nameMap.get(r.announcement_id);
      return {
        id: r.id,
        announcement_id: r.announcement_id,
        recipient_name: ann?.recipient_name || 'Someone',
        public_slug: ann?.public_slug || null,
        text: r.response_message || '',
        created_at: r.created_at,
        source: 'announcement_responses' as const,
      };
    }),
    ...threadMessages.map((m) => {
      const announcementId = threadAnnMap.get(m.thread_id) || '';
      const ann = nameMap.get(announcementId);
      return {
        id: m.id,
        announcement_id: announcementId,
        recipient_name: ann?.recipient_name || 'Someone',
        public_slug: ann?.public_slug || null,
        text: m.message || '',
        created_at: m.created_at,
        source: 'thread_messages' as const,
        thread_id: m.thread_id,
        sender_role: m.sender_role,
      };
    }),
  ].filter((item) => item.id && item.announcement_id);

  const seen = new Set<string>();
  return feed
    .filter((item) => {
      const key = `${item.source}:${item.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, limit);
}

export async function fetchAnnouncementReplyTimeline(
  userId: string,
  announcementId: string,
): Promise<AnnouncementReplyTimeline> {
  // Owner-only reads: wait for the sign-in token so they don't run signed-out.
  await ensureSession();
  const [{ data: announcement }, directReplies, legacyResponses, threads] = await Promise.all([
    (supabase as any)
      .from('announcements')
      .select('id, recipient_name, message, template, public_slug')
      .eq('id', announcementId)
      .eq('user_id', userId)
      .maybeSingle(),
    readRows<any>(
      'announcement_replies',
      (supabase as any)
        .from('announcement_replies')
        .select('id, reply_text, created_at')
        .eq('announcement_id', announcementId)
        .order('created_at', { ascending: true }),
    ),
    readRows<any>(
      'announcement_responses',
      (supabase as any)
        .from('announcement_responses')
        .select('id, response_message, created_at')
        .eq('announcement_id', announcementId)
        .order('created_at', { ascending: true }),
    ),
    readRows<any>(
      'conversation_threads',
      (supabase as any)
        .from('conversation_threads')
        .select('id')
        .eq('announcement_id', announcementId)
        .eq('announcement_owner_id', userId),
    ),
  ]);

  const threadIds = threads.map((t) => t.id).filter(Boolean);
  const threadMessages = threadIds.length
    ? await readRows<any>(
        'thread_messages',
        (supabase as any)
          .from('thread_messages')
          .select('id, thread_id, sender_role, message, created_at')
          .in('thread_id', threadIds)
          .order('created_at', { ascending: true }),
      )
    : [];

  const direct = [
    ...directReplies.map((r) => ({ id: r.id, reply_text: r.reply_text || '', created_at: r.created_at })),
    ...legacyResponses.map((r) => ({ id: r.id, reply_text: r.response_message || '', created_at: r.created_at })),
  ].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  return {
    announcement: announcement
      ? ({
          id: (announcement as any).id,
          recipient_name: (announcement as any).recipient_name,
          message: (announcement as any).message,
          template_name: (announcement as any).template,
          public_slug: (announcement as any).public_slug,
        } as AnnouncementReplyTimeline['announcement'])
      : null,
    directReplies: direct,
    threadMessages: threadMessages as AnnouncementReplyTimeline['threadMessages'],
  };
}